<template>
    <div class="challenge-filter">
        <div class="panel filter-panel text-left">
            <div class="search-container">
                <div class="search-input">
                    <form class="form-inline" onsubmit="return false">
                        <input id="challenge-search-box" type="text" class="form-control challenge-search"
                               v-model="searchInput" placeholder="Challenge suchen...">
                    </form>
                </div>
                <div class="filter-toggle">
                    <div class="btn btn-secondary btn-filter" @click="toggleShowFilter">
                        Filter
                        <i v-if="!showFilter" class="dev-chevron-down"></i>
                        <i v-if="showFilter" class="dev-chevron-up"></i>
                    </div>
                </div>
                <div>
                    <button class="btn reset-filter-button" type="button"
                            @click="resetFilters()" title="Filter zurücksetzen">
                        <i class="dev-filter-off"></i>
                    </button>
                </div>
            </div>
            <div :class="{collapsed: !showFilter}" class="other-filters">
                <div class="filter-title">
                    <strong>Schwierigkeit</strong>
                </div>
                <DifficultyFilter
                    @selectDifficulties="updateSelectDifficulties"
                    ref="difficultyFilter">
                </DifficultyFilter>
                <div class="filter-title">
                    <strong>Programmiersprache <span v-if="containsDocker">/ Technologie</span></strong>
                </div>
                <div class="language-filter-col">
                    <LanguageFilter
                        ref="languageFilter"
                        @selectLanguages="updateSelectLanguages"
                        :languages-retrieved-from-api="languagesRetrievedFromApi">
                    </LanguageFilter>
                </div>
                <div v-if="allChallengesCheckbox">
                    <div class="filter-title">
                        <strong>Optionen</strong>
                    </div>
                    <div class="form-group optionFilter">
                        <select @change="updateChallengeFilterState($event)"
                                class="form-control challenge-filter-state">
                            <option v-for="(challengeFilterState, index) in challengeFilterStates"
                                    :value="challengeFilterState.key" :key="'challengeFilter' + index"
                                    :selected="challengeFilterState.key === challengeFilterStateKey"
                            >
                                {{ challengeFilterState.label }}
                            </option>
                        </select>
                    </div>
                    <div class="show-sponsored-selection-wrapper">
                        <div class="show-sponsored-button"
                             :class="{'selected': showSponsored, 'isNotMobile': !isMobile()}"
                             @click="toggleShowSponsored()">
                            nur gesponserte Challenges anzeigen
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import LanguageFilter from "./LanguageFilter";
import DifficultyFilter from "./DifficultyFilter";
import {FilterObject} from "../../services/filtering";
import {debounce} from "lodash";

export default {
    name: "ChallengeFilter",
    components: {LanguageFilter, DifficultyFilter},
    props: {
        allChallengesCheckbox: {type: Boolean, default: false},
        languagesRetrievedFromApi: {
            type: Set, default: () => {
            }
        },
    },
    data() {
        return {
            showAll: false,
            showFilter: false,
            searchInput: "",
            challengeFilterStateKey: 'all',
            showSponsored: false,
            filterObject: new FilterObject(
                this.$router,
                this.$route,
                [
                    {'key': 'searchInput', 'default': ''},
                    {'key': 'difficultyFilter', 'default': ''},
                    {'key': 'languageFilter', 'default': ''},
                    {'key': 'challengeFilterStateKey', 'default': 'all'},
                    {'key': 'showSponsored', 'default': false},
                ],
                [
                    {'key': 'showFilter', 'default': false},
                ]
            ),
            challengeFilterStates: [
                {key: 'all', name: 'Alle', label: 'Alle Challenges', icon: 'dev-terminal-box',},
                {
                    key: 'available',
                    name: 'Verfügbare',
                    label: 'Noch nicht angenommene Challenges',
                    icon: 'dev-terminal-box',
                },
                {key: 'new', name: 'Neue verfügbare', label: 'Nur neue verfügbare Challenges', icon: 'fa-star',},
                {key: 'started', name: 'Gestartete', label: 'Nur gestartete Challenges', icon: 'dev-laptop',},
                {
                    key: 'solved',
                    name: 'Abgeschlossene',
                    label: 'Nur abgeschlossene Challenges',
                    icon: 'dev-circle-check',
                },
            ],
        }
    },
    computed: {
        containsDocker: function () {
            return this.languagesRetrievedFromApi.has('dockerfile');
        },
    },
    watch: {
        "searchInput": function () {
            this.updateSearchInput();
        },
        $route: function () {
            if (this.$route.query.challengeFilterStateKey !== undefined) {
                let challengeFilterState = this.getChallengeFilterState(this.$route.query.challengeFilterStateKey.toLowerCase());
                this.$emit("challengeFilterStateChange", challengeFilterState);
            }
        },
    },
    mounted() {
        this.showFilter = this.filterObject.filterProps.showFilter;
        this.searchInput = this.filterObject.filterData.searchInput;
        this.showSponsored = this.filterObject.filterData.showSponsored;
        if (this.$route.query.challengeFilterStateKey !== undefined) {
            const challengeFilterState = this.getChallengeFilterState(this.$route.query.challengeFilterStateKey.toLowerCase());
            this.$emit("challengeFilterStateChange", challengeFilterState);
        }
        this.filterObject.updateQuery();
    },
    methods: {
        getChallengeFilterState: function (key) {
            let challengeFilterState = this.challengeFilterStates.find((state) => state.key === key);
            if (!challengeFilterState) {
                challengeFilterState = this.challengeFilterStates[0];
            }
            this.challengeFilterStateKey = challengeFilterState.key;
            this.filterObject.filterData.challengeFilterStateKey = this.challengeFilterStateKey;
            return challengeFilterState;
        },
        resetFilters: function () {
            this.filterObject.reset();
            // reset text on input field
            this.searchInput = "";
            this.challengeFilterStateKey = 'all';
            this.showSponsored = false;
            this.filterObject.filterData.challengeFilterStateKey = 'all';
            this.filterObject.filterData.showSponsored = false;
            this.$refs.difficultyFilter.resetFilters();
            this.$refs.languageFilter.resetFilters();
            // showFilter should not be reset
            this.filterObject.filterProps.showFilter = this.showFilter;
            this.$emit('challengeFilterStateChange', this.challengeFilterStates[0]);
            this.filterObject.updateQuery();
        },
        toggleShowFilter: function () {
            this.showFilter = !this.showFilter;
            this.filterObject.filterProps.showFilter = this.showFilter;
            this.filterObject.updateQuery();
        },
        toggleShowSponsored: function () {
            this.showSponsored = !this.showSponsored;
            this.filterObject.filterData.showSponsored = this.showSponsored;
            this.filterObject.updateQuery();
            this.$emit("sponsoredChange", this.showSponsored);
        },
        isMobile: function () {
            return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        },
        updateSearchInput: debounce(function () {
            this.$emit('input', this.searchInput);
            this.filterObject.filterData.searchInput = this.searchInput;
            this.filterObject.updateQuery();
        }, 1000),
        updateSelectDifficulties: function (difficulties) {
            let selectedDifficulties = [];
            for (let difficulty of difficulties) {
                if (difficulty.selected) {
                    selectedDifficulties.push(difficulty.complexity);
                }
            }
            if (selectedDifficulties.length !== 0) {
                this.filterObject.filterData.difficultyFilter = selectedDifficulties.join(',');
            } else {
                this.filterObject.filterData.difficultyFilter = '';
            }
            this.filterObject.updateQuery();
        },
        updateSelectLanguages: function (languages) {
            let selectedLanguageKeys = [];
            for (let language of languages) {
                if (language.selected) {
                    selectedLanguageKeys.push(language.key);
                }
            }
            if (selectedLanguageKeys.length !== 0) {
                this.filterObject.filterData.languageFilter = selectedLanguageKeys.join(',');
            } else {
                this.filterObject.filterData.languageFilter = '';
            }
            this.filterObject.updateQuery();
        },
        updateChallengeFilterState: function (event) {
            let challengeFilterState = this.getChallengeFilterState(event.target.value);
            this.challengeFilterStateKey = challengeFilterState.key;
            this.filterObject.filterData.challengeFilterStateKey = this.challengeFilterStateKey;
            this.filterObject.updateQuery();
            this.$emit('challengeFilterStateChange', challengeFilterState);
        },
    },
}
</script>

<style scoped>
.panel.filter-panel {
    padding: 1rem;
}

.search-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1rem 0 1rem;
}

.search-input {
    flex-grow: 1;
}

.filter-toggle {
    padding: 0 0 0 1rem;
    flex-grow: 0;
}

.btn-filter {
    background-color: transparent;
    margin-right: 2rem;
    min-width: 88px;
}

.btn-filter:hover, .btn-filter:active, .selected {
    background-color: var(--button-background);
}

.form-inline input {
    background: #f7f9fa !important;
}

.other-filters {
    overflow: hidden;
    margin-top: 1rem;
    max-height: 400px;
    transition: max-height 0.3s ease-out;
}

.other-filters.collapsed {
    max-height: 0;
}

.challenge-filter {
    margin-top: 36px;
}

.challenge-filter-state {
    margin: 0 10px;
    width: 95%;
}

.optionFilter {
    margin: 1rem 0;
}

.filter-title {
    padding: 0 1rem;
}

.panel {
    padding: 2rem;
}

.challenge-search {
    padding: 2rem;
    margin: 0 .5rem 0 0;
    border-radius: 7px;
    border: none;
    max-width: 500px;
    width: 100%;
}

.show-sponsored-selection-wrapper {
    padding: 1rem;
}

.show-sponsored-button {
    color: #08AB99;
    background-color: white;
}

.show-sponsored-button.selected, .isNotMobile.show-sponsored-button:hover {
    color: white;
    background-color: #08AB99;
}

.show-sponsored-button i {
    margin-right: 1rem;
}

.show-sponsored-button {
    display: inline-block;
    padding: 4px 2rem;
    border-radius: 100px;
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
    -khtml-user-select: none; /* Konqueror HTML */
    -moz-user-select: none; /* Old versions of Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none;
    -webkit-transform: perspective(1px) translateZ(0);
    transform: perspective(1px) translateZ(0);
    box-shadow: 0 3px 8px rgba(13, 51, 85, 0.2);
    -webkit-transition-duration: 0.3s;
    transition-duration: 0.3s;
    -webkit-transition-property: box-shadow, transform;
    transition-property: box-shadow, transform;
}

.isNotMobile.show-sponsored-button:hover {
    cursor: pointer;
    box-shadow: 0 10px 10px -10px rgba(0, 0, 0, 0.5);
    -webkit-transform: scale(1.05);
    transform: scale(1.05);
}

::v-deep .language-filter-col .lang-selector {
    display: block;
    justify-content: left;
    text-align: left;
    margin: 1rem 0 1rem 0;
    padding-left: 1rem;
}

@media (max-width: 500px) {
    .challenge-search {
        width: 100%;
    }
}
</style>